import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import { useApiClient } from '../api/apiContext';
import { messageForError } from '../api/problem';
import { useAuthStore } from '../auth/authStore';
import { BrandMark } from '../components/BrandMark';
import { LanguageToggle } from '../components/LanguageToggle';

/**
 * Email and password, the first of up to three steps.
 *
 * The language toggle sits on this screen too: a staff member who cannot read the sign-in form
 * cannot reach the setting that would fix it.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const client = useApiClient();
  const location = useLocation();

  const phase = useAuthStore((state) => state.phase);
  const signIn = useAuthStore((state) => state.signIn);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  if (phase === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? '/'} replace />;
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await signIn(client, email, password);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="centered-page">
      <div className="centered-page__card panel">
        {/* Centred above the lockup rather than pushed into the card's corner. Flush to the end
            edge it crowded the padding and read as a control that had come loose from the sheet;
            on the axis the mark and the heading already share, it reads as the first choice the
            screen offers — which, for somebody who cannot read the language it opened in, it is. */}
        <div className="centered-page__lang">
          <LanguageToggle />
        </div>

        {/* The mark leads on the way in, the way it does on the patient app's
            sign-in: this is the one screen someone reaches before they know
            whose software they are looking at. */}
        <div className="sign-in__brand">
          <BrandMark size={52} />
          <span className="wordmark">{t('app.name')}</span>
        </div>

        <h1 style={{ marginBlock: 'var(--space-5) var(--space-2)' }}>{t('auth.signIn.title')}</h1>
        <p style={{ marginBlockEnd: 'var(--space-5)', color: 'var(--color-text-muted)' }}>
          {t('auth.signIn.subtitle')}
        </p>

        <form onSubmit={onSubmit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="email">
              {t('auth.signIn.email')}
            </label>
            <input
              className="field__input"
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              inputMode="email"
              dir="ltr"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="password">
              {t('auth.signIn.password')}
            </label>
            <input
              className="field__input"
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              dir="ltr"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {error ? (
            <p className="field__error" role="alert">
              {messageForError(error, t as (key: string, options?: { defaultValue?: string }) => string)}
            </p>
          ) : null}

          <button type="submit" className="button button--primary" disabled={submitting}>
            {submitting ? t('auth.signIn.submitting') : t('auth.signIn.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}
